import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import {
  formatSalesApiError,
  getSalesArrayPayload,
  useSalesEndpoints,
} from '../hooks/useSalesApiResource';
import {
  buildPartStationPayload,
  buildStationPartCategoryRows,
  filterAssignableStationParts,
  getBlockedStationPartRemovalIds,
  getStationPartAssignmentChanges,
  isCustomWriteInCategory,
  normalizeStationPart,
  normalizeStationPartAssignment,
  normalizeStationPartCategory,
} from './stationPartsUtils';
import { normalizePartMatchRule } from './partAutoSelectUtils';

const PART_PAGE_SIZE = 100;
const ALL_ASSIGNED_CATEGORIES = -1;

const StationPartsManager = ({ station, canEditSales, refreshVersion = 0 }) => {
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const [categories, setCategories] = useState([]);
  const [parts, setParts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [draftAssignedIds, setDraftAssignedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);
  const [pageSize, setPageSize] = useState(PART_PAGE_SIZE);
  const [saving, setSaving] = useState(false);
  const [selectedDisplayCategoryId, setSelectedDisplayCategoryId] = useState(ALL_ASSIGNED_CATEGORIES);
  const [displaySearchTerm, setDisplaySearchTerm] = useState('');
  const [displayPageSize, setDisplayPageSize] = useState(PART_PAGE_SIZE);
  const [blockedRemovalIds, setBlockedRemovalIds] = useState(new Set());

  const fetchAssignments = useCallback(async () => {
    const response = await request({
      method: 'GET',
      url: endpoints.partStations.base,
      params: { stationid: station.id },
    });
    return getSalesArrayPayload(response)
      .map(normalizeStationPartAssignment)
      .filter((assignment) => assignment.stationId === station.id);
  }, [endpoints, request, station.id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // These reads are deliberately sequential. The Sales API is sensitive
      // to bursts, and no configuration-builder resources are needed here.
      const categoryResponse = await request({ method: 'GET', url: endpoints.categories.base });
      const nextCategories = getSalesArrayPayload(categoryResponse).map(normalizeStationPartCategory);
      const partResponse = await request({ method: 'GET', url: endpoints.parts.base });
      const nextParts = getSalesArrayPayload(partResponse).map(normalizeStationPart);
      const nextAssignments = await fetchAssignments();
      setCategories(nextCategories);
      setParts(nextParts);
      setAssignments(nextAssignments);
    } catch (requestError) {
      setError(formatSalesApiError(requestError, 'Unable to load station parts.'));
    } finally {
      setLoading(false);
    }
  }, [endpoints, fetchAssignments, request]);

  useEffect(() => {
    load();
  }, [load, refreshVersion]);

  const currentAssignedIds = useMemo(
    () => new Set(assignments.map((assignment) => assignment.partId)),
    [assignments]
  );
  const visiblePartIds = useMemo(() => new Set(parts.map((part) => part.id)), [parts]);
  const visibleAssignedIds = useMemo(
    () => new Set([...currentAssignedIds].filter((partId) => visiblePartIds.has(partId))),
    [currentAssignedIds, visiblePartIds]
  );
  const effectiveDraftAssignedIds = useMemo(() => new Set([
    ...draftAssignedIds,
    ...[...currentAssignedIds].filter((partId) => !visiblePartIds.has(partId)),
  ]), [currentAssignedIds, draftAssignedIds, visiblePartIds]);
  const categoryRows = useMemo(
    () => buildStationPartCategoryRows(categories, parts, assignments, station.id),
    [assignments, categories, parts, station.id]
  );
  const categoryById = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories]
  );
  const sortedParts = useMemo(() => filterAssignableStationParts(parts, categories).sort((left, right) => {
    const leftCategory = categoryById.get(left.categoryId)?.name || '';
    const rightCategory = categoryById.get(right.categoryId)?.name || '';
    return leftCategory.localeCompare(rightCategory)
      || left.sortOrder - right.sortOrder
      || left.partNumber.localeCompare(right.partNumber, undefined, { numeric: true });
  }), [categories, categoryById, parts]);
  const filteredParts = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return sortedParts.filter((part) => {
      const category = categoryById.get(part.categoryId);
      const matchesCategory = !selectedCategoryId || part.categoryId === selectedCategoryId;
      const matchesSearch = !needle || [part.partNumber, part.name, category?.code, category?.name]
        .some((value) => String(value || '').toLowerCase().includes(needle));
      return matchesCategory && matchesSearch;
    });
  }, [categoryById, searchTerm, selectedCategoryId, sortedParts]);
  const displayedParts = filteredParts.slice(0, pageSize);
  const filteredActivePartCount = useMemo(
    () => filteredParts.reduce(
      (count, part) => count + (draftAssignedIds.has(part.id) ? 1 : 0),
      0
    ),
    [draftAssignedIds, filteredParts]
  );
  const changes = useMemo(
    () => getStationPartAssignmentChanges(currentAssignedIds, effectiveDraftAssignedIds),
    [currentAssignedIds, effectiveDraftAssignedIds]
  );
  const assignedCategoryRows = useMemo(
    () => categoryRows.filter((category) => category.activeParts.length > 0),
    [categoryRows]
  );
  const editableCategoryRows = useMemo(
    () => categoryRows.filter((category) => category.id && !isCustomWriteInCategory(category)),
    [categoryRows]
  );
  const selectedDisplayCategory = useMemo(
    () => assignedCategoryRows.find((category) => category.id === selectedDisplayCategoryId) || null,
    [assignedCategoryRows, selectedDisplayCategoryId]
  );
  const assignedPartsForDisplay = useMemo(() => {
    const rows = selectedDisplayCategory
      ? [selectedDisplayCategory]
      : assignedCategoryRows;
    return rows.flatMap((category) => category.activeParts.map((part) => ({
      ...part,
      displayCategory: category,
    })));
  }, [assignedCategoryRows, selectedDisplayCategory]);
  const filteredAssignedParts = useMemo(() => {
    const needle = displaySearchTerm.trim().toLowerCase();
    return assignedPartsForDisplay.filter((part) => (
      !needle || [part.partNumber, part.name, part.displayCategory.code, part.displayCategory.name]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    ));
  }, [assignedPartsForDisplay, displaySearchTerm]);
  const displayedAssignedParts = selectedDisplayCategory
    ? filteredAssignedParts
    : filteredAssignedParts.slice(0, displayPageSize);
  const displayedAssignedGroups = useMemo(() => {
    const filteredCounts = new Map();
    filteredAssignedParts.forEach((part) => {
      const categoryId = part.displayCategory.id;
      filteredCounts.set(categoryId, (filteredCounts.get(categoryId) || 0) + 1);
    });
    const groups = new Map();
    displayedAssignedParts.forEach((part) => {
      const categoryId = part.displayCategory.id;
      if (!groups.has(categoryId)) {
        groups.set(categoryId, {
          category: part.displayCategory,
          parts: [],
          filteredCount: filteredCounts.get(categoryId),
        });
      }
      groups.get(categoryId).parts.push(part);
    });
    return [...groups.values()];
  }, [displayedAssignedParts, filteredAssignedParts]);

  useEffect(() => {
    if (!assignedCategoryRows.length) {
      setSelectedDisplayCategoryId(ALL_ASSIGNED_CATEGORIES);
      return;
    }
    if (
      selectedDisplayCategoryId !== ALL_ASSIGNED_CATEGORIES
      && !assignedCategoryRows.some((category) => category.id === selectedDisplayCategoryId)
    ) {
      setSelectedDisplayCategoryId(ALL_ASSIGNED_CATEGORIES);
      setDisplaySearchTerm('');
      setDisplayPageSize(PART_PAGE_SIZE);
    }
  }, [assignedCategoryRows, selectedDisplayCategoryId]);

  const openEditor = (categoryId = 0) => {
    setDraftAssignedIds(new Set(visibleAssignedIds));
    setSearchTerm('');
    setSelectedCategoryId(isCustomWriteInCategory(categoryById.get(categoryId)) ? 0 : categoryId);
    setPageSize(PART_PAGE_SIZE);
    setBlockedRemovalIds(new Set());
    setError('');
    setNotice('');
    setShowEditor(true);
  };

  const togglePart = (partId, checked) => {
    setBlockedRemovalIds((current) => {
      if (!current.has(partId)) return current;
      const next = new Set(current);
      next.delete(partId);
      return next;
    });
    setDraftAssignedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(partId);
      else next.delete(partId);
      return next;
    });
  };

  const performAssignmentWrite = async (method, partId) => {
    // SP_PartStations is composite-keyed. DELETE requires the same PartId +
    // StationId object as POST and must never be reduced to a generic ID.
    const payload = buildPartStationPayload(partId, station.id);
    try {
      await request({
        method,
        url: endpoints.partStations.base,
        data: payload,
        contract: { requestCase: 'raw' },
        requestPolicy: { retry: false, timeoutMs: 20000 },
      });
      return;
    } catch (requestError) {
      // A timed-out composite-key write may still have committed. Verify the
      // station-scoped result before surfacing an error or retrying a write.
      try {
        const committedAssignments = await fetchAssignments();
        const exists = committedAssignments.some((assignment) => assignment.partId === partId);
        if ((method === 'POST' && exists) || (method === 'DELETE' && !exists)) return;
      } catch {
        // Preserve the write error when verification is unavailable.
      }
      throw requestError;
    }
  };

  const saveAssignments = async () => {
    if (changes.add.length === 0 && changes.remove.length === 0) {
      setShowEditor(false);
      return;
    }

    setSaving(true);
    setError('');
    setBlockedRemovalIds(new Set());

    if (changes.remove.length > 0) {
      try {
        const ruleResponse = await request({
          method: 'GET',
          url: endpoints.partMatchRules.base,
          requestPolicy: { retry: false, timeoutMs: 20000 },
        });
        const partMatchRules = getSalesArrayPayload(ruleResponse).map(normalizePartMatchRule);
        const blockedIds = getBlockedStationPartRemovalIds(changes.remove, partMatchRules, station.id);

        if (blockedIds.length > 0) {
          const blockedParts = blockedIds.map((partId) => {
            const part = parts.find((candidate) => candidate.id === partId);
            return part?.partNumber || part?.name || `Part ${partId}`;
          });
          setBlockedRemovalIds(new Set(blockedIds));
          setError(
            `Cannot deactivate ${blockedParts.join(', ')}. Delete ${blockedIds.length === 1 ? 'its' : 'their'} Part Auto Select rules in Step 7 before disabling ${blockedIds.length === 1 ? 'this part' : 'these parts'}.`
          );
          setSaving(false);
          return;
        }
      } catch (requestError) {
        setError(formatSalesApiError(
          requestError,
          'Unable to verify Part Auto Select rules. No station part assignments were changed.'
        ));
        setSaving(false);
        return;
      }
    }

    try {
      for (const partId of changes.add) await performAssignmentWrite('POST', partId);
      for (const partId of changes.remove) await performAssignmentWrite('DELETE', partId);
      setAssignments([...effectiveDraftAssignedIds].map((partId) => ({ partId, stationId: station.id })));
      setNotice(`${draftAssignedIds.size} ${draftAssignedIds.size === 1 ? 'part is' : 'parts are'} active for ${station.stationName}.`);
      setShowEditor(false);
    } catch (requestError) {
      try {
        setAssignments(await fetchAssignments());
      } catch {
        // Keep the last confirmed local state if recovery is unavailable.
      }
      setError(formatSalesApiError(requestError, 'Unable to save all station part assignments. Confirmed changes were retained; review the selections and try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="sales-loading"><Spinner animation="border" size="sm" /> Loading parts for {station.stationName}...</div>;
  }

  return (
    <>
      <div className="config-admin-page-heading">
        <div>
          <h4>Parts for {station.stationName}</h4>
          <p>Define the parts available to this station and review active-part counts by category. These parts are the eligible pool for Step 7 Part Auto Select.</p>
        </div>
        <Button onClick={() => openEditor()} disabled={!canEditSales}>
          <i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add part
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert variant="success" dismissible onClose={() => setNotice('')}>{notice}</Alert>}

      <div className="config-admin-parts-summary">
        <span><strong>{visibleAssignedIds.size}</strong> active parts</span>
        <span><strong>{assignedCategoryRows.length}</strong> active {assignedCategoryRows.length === 1 ? 'category' : 'categories'}</span>
      </div>

      {assignedCategoryRows.length === 0 ? (
        <div className="config-admin-empty">No parts are assigned to this station. Use Add part to activate parts.</div>
      ) : (
        <>
          <div className="config-admin-part-category-toolbar">
            <Form.Group>
              <Form.Label>Assigned category</Form.Label>
              <Form.Select
                value={selectedDisplayCategoryId}
                onChange={(event) => {
                  setSelectedDisplayCategoryId(Number(event.target.value));
                  setDisplaySearchTerm('');
                  setDisplayPageSize(PART_PAGE_SIZE);
                }}
              >
                <option value={ALL_ASSIGNED_CATEGORIES}>All assigned categories ({visibleAssignedIds.size})</option>
                {assignedCategoryRows.map((category) => (
                  <option key={category.id || 'uncategorized'} value={category.id}>
                    {category.code ? `${category.code} - ` : ''}{category.name} ({category.activeParts.length})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Search assigned parts</Form.Label>
              <Form.Control
                type="search"
                placeholder="Part number or name"
                value={displaySearchTerm}
                onChange={(event) => { setDisplaySearchTerm(event.target.value); setDisplayPageSize(PART_PAGE_SIZE); }}
              />
            </Form.Group>
          </div>

          {isCustomWriteInCategory(selectedDisplayCategory) && (
            <Alert variant="secondary">Custom Options are shown for reference but are not managed through station part assignments.</Alert>
          )}

          <Card>
            <Card.Header className="config-admin-part-list-heading">
              <strong>Assigned parts</strong>
              <Badge bg="success">{filteredAssignedParts.length} assigned</Badge>
            </Card.Header>
            <div className="table-responsive config-admin-assigned-part-list">
              <Table hover className="mb-0">
                {displayedAssignedGroups.length === 0 ? (
                  <tbody><tr><td colSpan={2}><div className="config-admin-empty">No assigned parts match the current search.</div></td></tr></tbody>
                ) : displayedAssignedGroups.map(({ category, parts: groupParts, filteredCount }) => (
                  <tbody key={category.id || 'uncategorized'}>
                    <tr className="config-admin-part-group-header">
                      <th colSpan={2} scope="rowgroup">
                        <div className="config-admin-part-group-heading">
                          <div className="config-admin-part-group-title">
                            {selectedDisplayCategory ? (
                              <span>{category.code ? `${category.code} - ` : ''}{category.name}</span>
                            ) : (
                              <Button
                                variant="link"
                                className="config-admin-category-drilldown"
                                onClick={() => {
                                  setSelectedDisplayCategoryId(category.id);
                                  setDisplaySearchTerm('');
                                  setDisplayPageSize(PART_PAGE_SIZE);
                                }}
                              >
                                {category.code ? `${category.code} - ` : ''}{category.name}
                              </Button>
                            )}
                            {canEditSales && category.id && !isCustomWriteInCategory(category) && (
                              <Button
                                variant="link"
                                className="config-admin-category-edit"
                                onClick={() => openEditor(category.id)}
                                aria-label={`Manage ${category.name}`}
                                title={`Manage ${category.name}`}
                              >
                                <i className="bi bi-pencil-square" aria-hidden="true"></i>
                              </Button>
                            )}
                          </div>
                          <Badge bg="secondary">
                            {filteredCount} {displaySearchTerm.trim() ? 'matching' : 'assigned'}
                          </Badge>
                        </div>
                      </th>
                    </tr>
                    <tr className="config-admin-part-group-columns">
                      <th scope="col">Part number</th>
                      <th scope="col">Part name</th>
                    </tr>
                    {groupParts.map((part) => (
                      <tr key={part.id}>
                        <td><strong>{part.partNumber || `Part ${part.id}`}</strong></td>
                        <td>{part.name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </Table>
            </div>
            {!selectedDisplayCategory && displayPageSize < filteredAssignedParts.length && (
              <Card.Footer className="text-center">
                <Button variant="outline-primary" size="sm" onClick={() => setDisplayPageSize((current) => current + PART_PAGE_SIZE)}>
                  Show {Math.min(PART_PAGE_SIZE, filteredAssignedParts.length - displayPageSize)} more
                </Button>
              </Card.Footer>
            )}
          </Card>
        </>
      )}

      <Modal show={showEditor} onHide={saving ? undefined : () => setShowEditor(false)} size="xl" centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{selectedCategoryId ? `Manage ${categoryById.get(selectedCategoryId)?.name || `category ${selectedCategoryId}`}` : `Assign parts to ${station.stationName}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Alert variant="info" className="mb-3">
            Activating a part makes it available to this station. It does not create an auto-pick rule; those rules will be managed in Step 7.
          </Alert>
          <div className="config-admin-part-filters">
            <Form.Group>
              <Form.Label>Search parts</Form.Label>
              <Form.Control
                type="search"
                placeholder="Part number, name, or category"
                value={searchTerm}
                onChange={(event) => { setSearchTerm(event.target.value); setPageSize(PART_PAGE_SIZE); }}
                autoFocus
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Select value={selectedCategoryId} onChange={(event) => { setSelectedCategoryId(Number(event.target.value)); setPageSize(PART_PAGE_SIZE); }}>
                <option value={0}>All categories</option>
                {editableCategoryRows.map((category) => (
                  <option key={category.id} value={category.id}>{category.code ? `${category.code} - ` : ''}{category.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          <div className="config-admin-part-selection-summary">
            <span><strong>{filteredActivePartCount.toLocaleString()}/{filteredParts.length.toLocaleString()}</strong> active</span>
          </div>

          <div className="table-responsive config-admin-part-picker">
            <Table hover className="mb-0">
              <thead><tr><th>Activate</th><th>Category</th><th>Part number</th><th>Part name</th></tr></thead>
              <tbody>
                {displayedParts.length === 0 ? (
                  <tr><td colSpan={4}><div className="config-admin-empty">No parts match the current filters.</div></td></tr>
                ) : displayedParts.map((part) => (
                  <tr key={part.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={draftAssignedIds.has(part.id)}
                        onChange={(event) => togglePart(part.id, event.target.checked)}
                        isInvalid={blockedRemovalIds.has(part.id)}
                        disabled={saving}
                        aria-label={`Activate ${part.partNumber || part.name || `part ${part.id}`}`}
                      />
                    </td>
                    <td>{categoryById.get(part.categoryId)?.name || `Category ${part.categoryId}`}</td>
                    <td><strong>{part.partNumber || `Part ${part.id}`}</strong></td>
                    <td>
                      {part.name || '—'}
                      {blockedRemovalIds.has(part.id) && <Badge bg="danger" className="ms-2">Rule must be deleted first</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {pageSize < filteredParts.length && (
            <div className="text-center mt-3">
              <Button variant="outline-primary" onClick={() => setPageSize((current) => current + PART_PAGE_SIZE)}>
                Show {Math.min(PART_PAGE_SIZE, filteredParts.length - pageSize)} more
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <small className="me-auto text-muted">{filteredParts.length.toLocaleString()} matching parts</small>
          <Button variant="outline-secondary" onClick={() => setShowEditor(false)} disabled={saving}>Cancel</Button>
          <Button onClick={saveAssignments} disabled={saving || (changes.add.length === 0 && changes.remove.length === 0)}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save assignments
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StationPartsManager;
